/**
 * /callbacks/:id — Callback detail (Story 7.13 + 7.13b refactor).
 *
 * Mostra:
 *   - badge de status + scheduled time + attempt count
 *   - timeline visual (CallbackAttemptTimeline)
 *   - link para conversation, campanha, customer (masked ou full p/ admin)
 *   - context hint, last_error, last_attempted_at
 *   - mensagem disparadora + mensagem enviada (se executed)
 *   - ações Cancel (operator+) e Retry (admin), em estados aplicáveis
 *
 * Refactor 7.13b extracted:
 *   - typed client → `lib/callbacks.ts`
 *   - hooks → `useCallbackDetail`, `useCancelCallback`, `useRetryCallback`
 *   - timeline → `CallbackAttemptTimeline`
 *   - confirm modais → `ConfirmActionModal`
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { CallbackAttemptTimeline } from '@/components/callbacks/CallbackAttemptTimeline';
import { CallbackStatusBadge } from '@/components/callbacks/CallbackStatusBadge';
import { ConfirmActionModal } from '@/components/callbacks/ConfirmActionModal';
import { useCallbackDetail, useCancelCallback, useRetryCallback } from '@/hooks/useCallbacks';
import { ApiError } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string; currentStatus?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

export function CallbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'viewer';

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [retryOpen, setRetryOpen] = useState(false);

  const detailQuery = useCallbackDetail(id);

  const cancelMutation = useCancelCallback(id, {
    onSuccess: () => {
      toast.success('Callback cancelado.');
      setCancelOpen(false);
      setCancelReason('');
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao cancelar callback.')),
  });

  const retryMutation = useRetryCallback(id, {
    onSuccess: () => {
      toast.success('Callback reenfileirado — próxima execução em breve.');
      setRetryOpen(false);
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao reexecutar callback.')),
  });

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4" data-testid="callback-detail-skeleton">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (detailQuery.error) {
    const err = detailQuery.error;
    const isNotFound = err instanceof ApiError && err.status === 404;
    return (
      <Card>
        <CardContent className="space-y-3 p-6 text-sm">
          {isNotFound ? (
            <>
              <p className="text-base font-medium">Callback não encontrado</p>
              <p className="text-muted-foreground">
                Este callback pode ter sido apagado ou nunca existiu.
              </p>
              <Button variant="outline" onClick={() => navigate('/callbacks')}>
                Voltar para a lista
              </Button>
            </>
          ) : (
            <p className="text-destructive">{humanizeError(err, 'Falha ao carregar callback.')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const cb = detailQuery.data;
  if (!cb) return null;

  const canCancel =
    (role === 'operator' || role === 'admin') &&
    (cb.executionStatus === 'pending' || cb.executionStatus === 'failed');
  const canRetry = role === 'admin' && cb.executionStatus === 'failed';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/callbacks')}
            className="-ml-2"
          >
            ← Voltar
          </Button>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Callback</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
            <CallbackStatusBadge status={cb.executionStatus} />
            <span>Agendado: {formatDateTime(cb.scheduledAt)}</span>
            <span>Tentativas: {cb.attemptCount}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {canCancel ? (
            <Button
              variant="outline"
              onClick={() => setCancelOpen(true)}
              disabled={cancelMutation.isPending}
              data-testid="callback-cancel-btn"
            >
              Cancelar
            </Button>
          ) : null}
          {canRetry ? (
            <Button
              onClick={() => setRetryOpen(true)}
              disabled={retryMutation.isPending}
              data-testid="callback-retry-btn"
            >
              Reexecutar
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Linha do tempo</CardTitle>
        </CardHeader>
        <CardContent>
          <CallbackAttemptTimeline
            scheduledAt={cb.scheduledAt}
            lastAttemptedAt={cb.lastAttemptedAt}
            executedAt={cb.executedAt}
            cancelled={cb.cancelled}
            cancelledReason={cb.cancelledReason}
            attemptCount={cb.attemptCount}
            lastError={cb.lastError}
            status={cb.executionStatus}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contexto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Conversa:</span>
              <Link
                to={`/conversations/${cb.conversation.id}`}
                className="font-mono text-xs text-primary hover:underline"
              >
                abrir →
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Campanha:</span>
              <Link to={`/campaigns/${cb.campaign.id}`} className="text-primary hover:underline">
                {cb.campaign.name}
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente:</span>
              <span className="font-mono text-xs">{cb.customer.maskedPhone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Confiança:</span>
              <span>{cb.confidence ?? '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Dica de contexto:</span>
              <p className="mt-1 text-sm italic">{cb.contextHint ?? '—'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Log de execução</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <CallbackStatusBadge status={cb.executionStatus} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Última tentativa:</span>
              <span>{formatDateTime(cb.lastAttemptedAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Executado em:</span>
              <span>{formatDateTime(cb.executedAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cancelado:</span>
              <span>{cb.cancelled ? 'sim' : 'não'}</span>
            </div>
            {cb.cancelledReason ? (
              <div>
                <span className="text-muted-foreground">Motivo do cancelamento:</span>
                <p className="mt-1 text-xs">{cb.cancelledReason}</p>
              </div>
            ) : null}
            {cb.lastError ? (
              <div>
                <span className="text-muted-foreground">Último erro:</span>
                <pre className="mt-1 overflow-x-auto rounded bg-destructive/10 p-2 text-xs text-destructive">
                  {cb.lastError}
                </pre>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {cb.triggeringMessage ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mensagem que pediu o callback</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="whitespace-pre-wrap">{cb.triggeringMessage.content}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {formatDateTime(cb.triggeringMessage.createdAt)} ({cb.triggeringMessage.direction})
            </p>
          </CardContent>
        </Card>
      ) : null}

      {cb.executionMessage ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mensagem enviada na execução</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="whitespace-pre-wrap">{cb.executionMessage.content}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {formatDateTime(cb.executionMessage.createdAt)}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <ConfirmActionModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancelar callback?"
        description="A mensagem não será enviada. O cliente continuará aguardando até que uma nova conversa inicie."
        confirmLabel="Cancelar callback"
        confirmVariant="destructive"
        pendingLabel="Cancelando..."
        isPending={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate(cancelReason.trim())}
      >
        <div className="space-y-2">
          <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
          <Input
            id="cancel-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="ex.: cliente entrou em contato por outro canal"
            maxLength={200}
          />
        </div>
      </ConfirmActionModal>

      <ConfirmActionModal
        open={retryOpen}
        onOpenChange={setRetryOpen}
        title="Reexecutar callback?"
        description="O callback será reenfileirado com `scheduled_at = agora`. O worker pega no próximo tick (≤60s)."
        confirmLabel="Confirmar reexecução"
        pendingLabel="Reexecutando..."
        isPending={retryMutation.isPending}
        onConfirm={() => retryMutation.mutate()}
      />
    </div>
  );
}
